import Phaser from 'phaser';
import { BRAND } from '../config';

// Compass conventions:
// 0deg = North (up), 90 = East (right), 180 = South (down), 270 = West (left).
// Returns a canvas-space unit vector pointing from the centre TO the sun
// (i.e. azimuth 180 = south = (0, +1) in canvas y-down coords).
export function azimuthToSunVector(deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: Math.sin(rad), y: -Math.cos(rad) };
}

/**
 * Sun compass widget.
 *
 * Splits the rendering into separate Phaser GameObjects so each can be
 * tweened independently:
 *   - `compassGfx` — the static dial (ring + cardinal labels + arrow stem)
 *   - `halo` — the soft amber halo behind the sun (pulses)
 *   - `rays` — short ray ticks around the sun (slow rotation)
 *   - `body` — the sun disc with highlight (gentle scale pulse)
 */
export class Sun {
  scene: Phaser.Scene;
  azimuthDeg: number;
  centre: { x: number; y: number };
  radius: number;

  compassGfx!: Phaser.GameObjects.Graphics;
  halo!: Phaser.GameObjects.Graphics;
  rays!: Phaser.GameObjects.Graphics;
  body!: Phaser.GameObjects.Graphics;
  label!: Phaser.GameObjects.Text;

  constructor(
    scene: Phaser.Scene,
    azimuthDeg: number,
    centre: { x: number; y: number },
    radius = 70,
  ) {
    this.scene = scene;
    this.azimuthDeg = azimuthDeg;
    this.centre = centre;
    this.radius = radius;
  }

  draw() {
    if (!this.compassGfx) {
      this.compassGfx = this.scene.add.graphics();
      this.halo = this.scene.add.graphics();
      this.rays = this.scene.add.graphics();
      this.body = this.scene.add.graphics();
      this.label = this.scene.add
        .text(this.centre.x, this.centre.y + this.radius + 18, '', {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '13px',
          color: '#ebebeb',
          fontStyle: 'bold',
        })
        .setOrigin(0.5, 0);
    }
    this.compassGfx.clear();
    this.halo.clear();
    this.rays.clear();
    this.body.clear();

    const { x: cx, y: cy } = this.centre;
    const r = this.radius;

    // Compass ring + inner ring
    this.compassGfx.lineStyle(2, BRAND.greyDark, 1);
    this.compassGfx.strokeCircle(cx, cy, r);
    this.compassGfx.lineStyle(1, BRAND.greyDark, 0.4);
    this.compassGfx.strokeCircle(cx, cy, r * 0.6);

    // Cardinal labels
    if (!this._cardinalsDrawn) {
      const opts = {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '11px',
        color: '#a1a1a1',
        fontStyle: 'bold',
      };
      this.scene.add.text(cx, cy - r - 12, 'N', opts).setOrigin(0.5);
      this.scene.add.text(cx + r + 12, cy, 'E', opts).setOrigin(0.5);
      this.scene.add.text(cx, cy + r + 4, 'S', opts).setOrigin(0.5);
      this.scene.add.text(cx - r - 12, cy, 'W', opts).setOrigin(0.5);
      this._cardinalsDrawn = true;
    }

    // Sun position on the compass ring at the actual compass bearing
    const v = azimuthToSunVector(this.azimuthDeg);
    const sunX = cx + v.x * r;
    const sunY = cy + v.y * r;

    // Halo (drawn relative to sun position; offsetted via setPosition on `halo`)
    for (let i = 5; i >= 1; i--) {
      this.halo.fillStyle(BRAND.amber, 0.05 * i);
      this.halo.fillCircle(0, 0, 13 + i * 4);
    }
    this.halo.setPosition(sunX, sunY);

    // Rays — drawn around (0,0) so we can rotate them
    this.rays.lineStyle(1.5, BRAND.amber, 0.85);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      this.rays.lineBetween(Math.cos(a) * 14, Math.sin(a) * 14, Math.cos(a) * 22, Math.sin(a) * 22);
    }
    this.rays.setPosition(sunX, sunY);

    // Sun body — drawn around (0,0), positioned via setPosition (so scale pulses
    // around the sun's centre, not the canvas origin)
    this.body.fillStyle(BRAND.amber, 1);
    this.body.fillCircle(0, 0, 12);
    this.body.fillStyle(BRAND.amberSoft, 0.75);
    this.body.fillCircle(-2, -2, 3);
    this.body.setPosition(sunX, sunY);

    // Light direction arrow (drawn into compassGfx so it doesn't move with the body)
    this.compassGfx.lineStyle(3, BRAND.orange, 1);
    const arrowEndX = cx - v.x * (r * 0.35);
    const arrowEndY = cy - v.y * (r * 0.35);
    this.compassGfx.lineBetween(sunX, sunY, arrowEndX, arrowEndY);
    const headSize = 10;
    const headAngle = Math.atan2(-v.y, -v.x);
    this.compassGfx.fillStyle(BRAND.orange, 1);
    this.compassGfx.fillTriangle(
      arrowEndX,
      arrowEndY,
      arrowEndX - Math.cos(headAngle - 0.4) * headSize,
      arrowEndY - Math.sin(headAngle - 0.4) * headSize,
      arrowEndX - Math.cos(headAngle + 0.4) * headSize,
      arrowEndY - Math.sin(headAngle + 0.4) * headSize,
    );

    this.label.setText(`Sun ${this.cardinalLabel()}`);
  }

  /**
   * Wire up the standard sun animations: pulsing halo, slow ray rotation,
   * subtle body scale pulse. Safe to call once per scene.
   */
  startAnimations() {
    if (!this.body) return;
    this.scene.tweens.add({
      targets: this.halo,
      alpha: { from: 0.7, to: 1 },
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.scene.tweens.add({
      targets: this.rays,
      angle: 360,
      duration: 24000,
      repeat: -1,
      ease: 'Linear',
    });
    this.scene.tweens.add({
      targets: this.body,
      scale: { from: 0.94, to: 1.06 },
      duration: 1700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private _cardinalsDrawn = false;

  cardinalLabel(): string {
    const names = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const idx = Math.round(this.azimuthDeg / 45) % 8;
    return names[idx];
  }
}
